import React from 'react'
import { useTranslation } from 'react-i18next'
import ConfirmModal from '../confirm-modal'
import { AllianceRequestProfile, AllianceRequestMoreNote } from './allianceRequestModalShared'
import type { CastleConquerRequestItem } from '../../common/allianceApi'

interface AllianceConquerRequestModalProps {
  open: boolean
  allianceName?: string
  requests: CastleConquerRequestItem[]
  busyId?: string | null
  onApprove: (requestId: string) => void
  onDecline: (requestId: string) => void
  onClose: () => void
}

/**
 * Leader-side castle-conquer approval, rendered with the game's shared ConfirmModal.
 * Shown one request at a time: Approve joins the defeated castle to the alliance
 * (reversing the winner's raid loot); Decline lets the winner keep the loot.
 * Driven by AllianceConquerRequestWatcher; closing (X / backdrop) only dismisses.
 */
export const AllianceConquerRequestModal: React.FC<AllianceConquerRequestModalProps> = ({
  open,
  allianceName,
  requests,
  busyId,
  onApprove,
  onDecline,
  onClose,
}) => {
  const { t } = useTranslation()
  const req = requests[0]
  if (!open || !req) return null

  const busy = busyId === req._id

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      onConfirm={() => onApprove(req._id)}
      onCancel={() => onDecline(req._id)}
      title={t('alliance.conquerRequestTitle', { defaultValue: 'Conquer Request' })}
      confirmText={t('alliance.approve', { defaultValue: 'Approve' })}
      cancelText={t('alliance.decline', { defaultValue: 'Decline' })}
      isProcessing={busy}
      content={
        <div className="flex w-full flex-col items-center gap-3 text-center">
          <AllianceRequestProfile req={req} />
          <p className="confirm-modal__message">
            {t('alliance.conquerRequestMessage', {
              defaultValue:
                'wants to conquer {{owner}}’s castle for {{name}}. Approve to join it to your alliance (their raid resources are returned).',
              owner: req.defeatedOwnerName,
              name: allianceName || t('alliance.alliance', { defaultValue: 'your alliance' }),
            })}
            {requests.length > 1 ? (
              <>
                <br />
                <AllianceRequestMoreNote remaining={requests.length} />
              </>
            ) : null}
          </p>
        </div>
      }
    />
  )
}

export default AllianceConquerRequestModal
